import { Marquee } from '@src/components/marquee-animation/MarqueeAnimation.component'
import classNames from 'classnames'
import { type ReactNode, useMemo } from 'react'
import styles from './RotatingUsp.module.scss'

interface RotatingUspProps {
  backgroundColor?: 'default' | 'grey'
  uspsList: string[]
  theme: MarqueeTheme
  /** Styling for the words */
  textWeight?: TextWeightType
  /** Whether the marquee words should have an icon in middle. This will use the url of icon. */
  decorativeIcon?: string
}

export function RotatingUsp({
  backgroundColor,
  uspsList,
  theme,
  textWeight = TextWeightType.StartRegular,
  decorativeIcon,
}: RotatingUspProps): ReactNode {
  const sectionClass = classNames(styles.RotatingUsp__container, {
    [styles.RotatingUsp___grey]: backgroundColor === 'grey',
    [styles.RotatingUsp___page]: theme === MarqueeTheme.Page,
    [styles.RotatingUsp___component]: theme === MarqueeTheme.Component,
  })

  function createUspData(uspList: string[], icon?: string): USP[] {
    return uspList.map(text => ({
      text,
      icon: icon || null,
    }))
  }

  const usps: USP[] = useMemo(() => {
    return createUspData(uspsList, decorativeIcon)
  }, [uspsList, decorativeIcon])

  return (
    <div className={styles.RotatingUsp}>
      <Marquee autoFill={true}>
        <div className={sectionClass}>
          {usps.map(({ text, icon }, index) => (
            <p
              className={classNames(styles.RotatingUsp__text, {
                [styles.RotatingUsp__text___regular]:
                  (textWeight === TextWeightType.StartBold && index % 2 === 1) ||
                  (textWeight === TextWeightType.StartRegular && index % 2 === 0) ||
                  textWeight === TextWeightType.Regular,
              })}
              key={String(text)}
            >
              {text}
              {icon && <img className={styles.RotatingUsp__icon} src={icon} alt="" aria-hidden />}
            </p>
          ))}
        </div>
      </Marquee>
    </div>
  )
}

interface USP {
  text: string
  icon: string | null
}

export enum MarqueeTheme {
  Component = 'Component',
  Footer = 'Footer',
  Page = 'Page',
}

export enum TextWeightType {
  StartBold = 'StartBold',
  StartRegular = 'StartRegular',
  Regular = 'Regular',
  Bold = 'Bold',
}
